import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { DollarSign, ArrowLeft, ChevronRight, Loader, Youtube } from 'lucide-react';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

export default function AdminFinancePage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [yearTotal, setYearTotal] = useState({});
  const [ytData, setYtData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async (y) => {
    setLoading(true);
    try {
      const [fin, yt] = await Promise.all([
        api.get(`/admin/finance?year=${y}`),
        api.get(`/youtube-adsense/admin/all?year=${y}`).catch(() => ({ data: { entries: [] } }))
      ]);
      setMonthly(fin.data.monthly || []);
      setYearTotal(fin.data.yearTotal || {});
      setYears(fin.data.years || []);
      setYtData(yt.data.entries || []);
    } catch (e) { toast.error('Errore'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(year); }, [year]);

  const openMonth = async (m, y) => {
    setDetailLoading(true);
    try {
      const [fin, yt] = await Promise.all([
        api.get(`/admin/finance/${m}/${y}`),
        api.get(`/youtube-adsense/admin/all?year=${y}`).catch(() => ({ data: { entries: [] } }))
      ]);
      const ytMonth = (yt.data.entries || []).filter(e => e.period_month === m);
      setDetail({ ...fin.data, ytEntries: ytMonth });
    } catch (e) { toast.error('Errore'); }
    finally { setDetailLoading(false); }
  };

  const fmt = (v) => `€${parseFloat(v || 0).toFixed(2)}`;

  // YT totals for the year
  const ytYearTotal = {
    gross: ytData.reduce((s, e) => s + parseFloat(e.gross_amount || 0), 0),
    pn: ytData.reduce((s, e) => s + parseFloat(e.proudnoize_share || 0), 0),
    user: ytData.reduce((s, e) => s + parseFloat(e.user_share || 0), 0)
  };

  // Combined totals
  const combinedPn = parseFloat(yearTotal.pn_share || 0) + ytYearTotal.pn;
  const combinedLabel = parseFloat(yearTotal.label_share || 0) + ytYearTotal.user;

  // YT per month lookup
  const ytByMonth = {};
  ytData.forEach(e => {
    if (!ytByMonth[e.period_month]) ytByMonth[e.period_month] = { gross: 0, pn: 0, user: 0 };
    ytByMonth[e.period_month].gross += parseFloat(e.gross_amount || 0);
    ytByMonth[e.period_month].pn += parseFloat(e.proudnoize_share || 0);
    ytByMonth[e.period_month].user += parseFloat(e.user_share || 0);
  });

  // Detail view
  if (detail) {
    const ytMonthTotal = {
      gross: (detail.ytEntries || []).reduce((s, e) => s + parseFloat(e.gross_amount || 0), 0),
      pn: (detail.ytEntries || []).reduce((s, e) => s + parseFloat(e.proudnoize_share || 0), 0),
      user: (detail.ytEntries || []).reduce((s, e) => s + parseFloat(e.user_share || 0), 0)
    };
    const detailCombinedPn = parseFloat(detail.totals?.pn_share || 0) + ytMonthTotal.pn;
    const detailCombinedLabel = parseFloat(detail.totals?.label_share || 0) + ytMonthTotal.user;

    return (
      <div>
        <button onClick={() => setDetail(null)} className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 text-sm"><ArrowLeft size={16} />Torna al riepilogo</button>
        <h1 className="text-2xl font-bold text-white mb-4">{MONTHS[detail.month - 1]} {detail.year}</h1>

        <div className="grid grid-cols-5 gap-3 mb-4">
          <div className="bg-gray-800 rounded-xl p-4 text-center"><p className="text-[10px] text-gray-500 uppercase">Lordo Streaming</p><p className="text-white font-bold text-lg">{fmt(detail.totals?.gross)}</p></div>
          <div className="bg-gray-800 rounded-xl p-4 text-center"><p className="text-[10px] text-gray-500 uppercase">Fee Distr.</p><p className="text-red-400 font-bold text-lg">{fmt(detail.totals?.dist_fee)}</p></div>
          <div className="bg-gray-800 rounded-xl p-4 text-center"><p className="text-[10px] text-gray-500 uppercase">Netto</p><p className="text-blue-400 font-bold text-lg">{fmt(detail.totals?.net)}</p></div>
          <div className="bg-yellow-900/30 rounded-xl p-4 text-center border border-yellow-800"><p className="text-[10px] text-yellow-500 uppercase">Proud Noize</p><p className="text-yellow-400 font-bold text-xl">{fmt(detailCombinedPn)}</p></div>
          <div className="bg-green-900/30 rounded-xl p-4 text-center border border-green-800"><p className="text-[10px] text-green-500 uppercase">Label</p><p className="text-green-400 font-bold text-xl">{fmt(detailCombinedLabel)}</p></div>
        </div>

        {/* YT AdSense for this month */}
        {detail.ytEntries?.length > 0 && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
            <p className="text-xs text-red-400 font-bold uppercase mb-2"><Youtube size={14} className="inline mr-1" />YouTube AdSense incluso</p>
            {detail.ytEntries.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <span className="text-gray-300">{e.first_name} {e.last_name}</span>
                <span className="text-gray-400">Lordo: €{parseFloat(e.gross_amount).toFixed(2)} · PN: €{parseFloat(e.proudnoize_share).toFixed(2)} · Utente: <span className="text-green-400 font-bold">€{parseFloat(e.user_share).toFixed(2)}</span></span>
              </div>
            ))}
          </div>
        )}

        <h2 className="text-sm font-bold text-gray-400 uppercase mb-3">Dettaglio per Utente ({detail.users?.length})</h2>
        <div className="space-y-2">
          {(detail.users || []).map(u => {
            const ytUser = (detail.ytEntries || []).filter(e => e.user_id === u.user_id);
            const ytUserShare = ytUser.reduce((s, e) => s + parseFloat(e.user_share || 0), 0);
            return (
              <div key={u.id} className="bg-gray-800 rounded-lg p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center font-bold text-sm flex-shrink-0">{(u.first_name||'?')[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{u.first_name} {u.last_name}</p>
                  <p className="text-[10px] text-gray-500">{u.email} · PN {u.pn_commission_percent}%</p>
                </div>
                <div className="text-right"><p className="text-xs text-orange-500">PN Totale</p><p className="text-orange-400 font-bold">{fmt(parseFloat(u.proudnoize_share) + ytUser.reduce((s, e) => s + parseFloat(e.proudnoize_share || 0), 0))}</p></div>
                <div className="text-right"><p className="text-xs text-green-500">Streaming</p><p className="text-green-400 font-bold">{fmt(u.user_share)}</p></div>
                {ytUserShare > 0 && <div className="text-right"><p className="text-xs text-red-500">YouTube</p><p className="text-red-400 font-bold">{fmt(ytUserShare)}</p></div>}
                <div className="text-right border-l border-gray-700 pl-4"><p className="text-xs text-white">Totale</p><p className="text-white font-bold text-lg">{fmt(parseFloat(u.user_share) + ytUserShare)}</p></div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><DollarSign size={24} className="text-yellow-400" />Guadagni</h1>
        <select className="input-field-dark w-32" value={year} onChange={e => setYear(parseInt(e.target.value))}>
          {(years.length ? years : [new Date().getFullYear()]).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? <Loader size={24} className="animate-spin text-yellow-400 mx-auto mt-20" /> : (<>
        {/* Year totals */}
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div className="bg-gray-800 rounded-xl p-4 text-center"><p className="text-[10px] text-gray-500 uppercase">Lordo Streaming</p><p className="text-white font-bold text-xl">{fmt(yearTotal.gross)}</p></div>
          <div className="bg-gray-800 rounded-xl p-4 text-center"><p className="text-[10px] text-gray-500 uppercase">Netto</p><p className="text-blue-400 font-bold text-xl">{fmt(yearTotal.net)}</p></div>
          <div className="bg-yellow-900/30 rounded-xl p-5 text-center border border-yellow-800"><p className="text-[10px] text-yellow-500 uppercase">Proud Noize {year}</p><p className="text-yellow-400 font-bold text-2xl">{fmt(combinedPn)}</p></div>
          <div className="bg-green-900/30 rounded-xl p-5 text-center border border-green-800"><p className="text-[10px] text-green-500 uppercase">Label {year}</p><p className="text-green-400 font-bold text-2xl">{fmt(combinedLabel)}</p></div>
        </div>

        {ytYearTotal.gross > 0 && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 mb-6 flex items-center justify-between">
            <span className="text-xs text-red-400"><Youtube size={14} className="inline mr-1" />YouTube AdSense {year}</span>
            <span className="text-sm text-gray-300">Lordo: €{ytYearTotal.gross.toFixed(2)} · PN: <span className="text-orange-400">€{ytYearTotal.pn.toFixed(2)}</span> · Label: <span className="text-green-400">€{ytYearTotal.user.toFixed(2)}</span></span>
          </div>
        )}

        {/* Monthly */}
        <h2 className="text-sm font-bold text-gray-400 uppercase mb-3">Mesi ({monthly.length})</h2>
        {monthly.length === 0 ? <p className="text-gray-500 text-center py-12">Nessun rendiconto per il {year}</p> : (
          <div className="space-y-2">
            {monthly.map(m => {
              const ytM = ytByMonth[m.period_month] || { gross: 0, pn: 0, user: 0 };
              return (
                <div key={m.period_month} onClick={() => openMonth(m.period_month, m.period_year)} className="bg-gray-800 rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-700 transition">
                  <div className="w-12 h-12 rounded-xl bg-blue-900/30 text-blue-400 flex items-center justify-center font-bold text-sm flex-shrink-0">{String(m.period_month).padStart(2, '0')}</div>
                  <div className="flex-1">
                    <p className="text-white font-bold">{MONTHS[m.period_month - 1]}</p>
                    <p className="text-[10px] text-gray-500">{m.statement_count} rendiconti{ytM.gross > 0 ? ' + YT' : ''}</p>
                  </div>
                  <div className="text-right"><p className="text-[10px] text-orange-500">PN</p><p className="text-orange-400 font-bold">{fmt(parseFloat(m.pn_share) + ytM.pn)}</p></div>
                  <div className="text-right"><p className="text-[10px] text-green-500">Label</p><p className="text-green-400 font-bold">{fmt(parseFloat(m.label_share) + ytM.user)}</p></div>
                  {ytM.gross > 0 && <Youtube size={14} className="text-red-500" />}
                  <ChevronRight size={16} className="text-gray-600" />
                </div>
              );
            })}
          </div>
        )}
      </>)}
    </div>
  );
}
